<script setup>
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { getInboxIconByType } from 'dashboard/helper/inbox.js';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

// Log básico para verificar si el componente se carga
const router = useRouter();
const route = useRoute();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value;
  return getInboxIconByType(channelType, medium);
});

const isWhatsAppChannel = computed(() => {
  const result =
    // WhatsApp API estándar (Channel::Whatsapp)
    inbox.value.channelType === 'Channel::Whatsapp' ||
    // WAHA API (Channel::Api con identificador waha_)
    (inbox.value.channelType === 'Channel::Api' &&
      ((inbox.value.inboxIdentifier || inbox.value.identifier)?.startsWith(
        'waha_'
      ) ||
        inbox.value.name?.toLowerCase().includes('whatsapp'))) ||
    // Twilio WhatsApp (Channel::TwilioSms con medium whatsapp)
    (inbox.value.channelType === 'Channel::TwilioSms' &&
      inbox.value.medium === 'whatsapp');

  return result;
});

const getChannelColorStyle = computed(() => {
  const channelType = inbox.value?.channelType;
  const name = (inbox.value?.name || '').toLowerCase();

  // WhatsApp - verde
  if (isWhatsAppChannel.value) {
    return '#22c55e';
  }

  // Facebook Messenger - azul
  if (channelType === 'Channel::FacebookPage') {
    return '#1877f2';
  }

  // Facebook Comments - azul
  if (channelType === 'Channel::FacebookComments') {
    return '#1877f2';
  }

  // Twitter/X - negro/azul oscuro
  if (channelType === 'Channel::TwitterProfile') {
    return '#111827';
  }

  // Instagram - rojo
  if (channelType === 'Channel::Instagram') {
    return '#dc2626';
  }

  // Telegram - celeste
  if (channelType === 'Channel::Telegram') {
    return '#38bdf8';
  }

  // Email - naranja
  if (channelType === 'Channel::Email') {
    return '#ea580c';
  }

  // Web Widget - azul
  if (channelType === 'Channel::WebWidget') {
    return '#3b82f6';
  }

  // Line - verde
  if (channelType === 'Channel::Line') {
    return '#16a34a';
  }

  // TikTok - rojo
  if (channelType === 'Channel::Tiktok') {
    return '#dc2626';
  }

  // SMS/Voz - gris
  if (
    channelType === 'Channel::Sms' ||
    channelType === 'Channel::TwilioSms' ||
    channelType === 'Channel::Voice'
  ) {
    return '#6b7280';
  }

  // API - verificar por nombre
  if (channelType === 'Channel::Api') {
    if (name.includes('whatsapp')) return '#22c55e';
    if (name.includes('facebook')) return '#1877f2';
    if (name.includes('instagram')) return '#dc2626';
    if (name.includes('telegram')) return '#38bdf8';
    return '#6b7280';
  }

  // Default para cualquier otro canal
  return '#6b7280';
});
const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = props.conversation;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

const onCardClick = e => {
  // Debug: Verificar que inbox tiene datos
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-4 px-3 py-5 transition-all duration-300 ease-in-out cursor-pointer"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="32"
      :status="currentContactStatus"
      :inbox="inbox"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 size-3"
              :style="{
                color: getChannelColorStyle,
                fill: getChannelColorStyle,
              }"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>
